/**
 * Ordered moment timeline for the selected statement: the AI's rationale on
 * top, then supports/contradicts cards by time, top-3 then expand.
 */
import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { EvidenceFeedbackReason, EvidenceFeedbackVerdict, SofAiRating } from '@/types/mentorEngagements';
import { mentorStyles } from '../shared';
import { EvidenceMomentCard } from './EvidenceMomentCard';
import { LOW_CONFIDENCE, countKept, relevanceState, visibleMoments, type EvidenceMoment, type RelevanceLookup } from './model';

interface Props {
  statementId: string;
  ai: SofAiRating | undefined;
  moments: EvidenceMoment[];
  lookup: RelevanceLookup;
  expanded: boolean;
  locked: boolean;
  recorded: boolean;
  transcriptAvailable: boolean;
  allRejected: boolean;
  onToggleExpanded: () => void;
  onVerdict: (moment: EvidenceMoment, verdict: EvidenceFeedbackVerdict, reason?: EvidenceFeedbackReason) => void;
  onReopen: (moment: EvidenceMoment) => void;
  onJump: (moment: EvidenceMoment) => void;
}

export function EvidenceTimeline({ statementId, ai, moments, lookup, expanded, locked, recorded, transcriptAvailable, allRejected, onToggleExpanded, onVerdict, onReopen, onJump }: Props) {
  const { t } = useTranslation();
  const lowConfidence = ai?.confidence != null && ai.confidence < LOW_CONFIDENCE;
  const [whyOpen, setWhyOpen] = useState<boolean | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const showWhy = whyOpen ?? lowConfidence;
  const { shown, hidden } = visibleMoments(moments, expanded);
  const collapsible = visibleMoments(moments, false).hidden > 0;
  const kept = countKept(moments, lookup);

  const handleVerdict = (moment: EvidenceMoment, verdict: EvidenceFeedbackVerdict, reason?: EvidenceFeedbackReason) => {
    if (verdict === 'not_relevant' && !reason) { setPendingKey(current => current === moment.key ? null : moment.key); return; }
    setPendingKey(null);
    onVerdict(moment, verdict, reason);
  };

  return (
    <View testID={`sof_review_evidence_${statementId}`} style={styles.wrap}>
      <View style={styles.titleRow}>
        <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.review.evidence.title')}</Text>
        {moments.length ? <Text style={styles.count}>{t('mentorEngagements.review.evidence.kept', { kept, total: moments.length })}</Text> : null}
      </View>
      {ai?.evidence_precision != null ? <Text style={mentorStyles.mutedText}>{t('mentorEngagements.review.evidence.precision', { percent: Math.round(ai.evidence_precision * 100) })}</Text> : null}

      {allRejected ? (
        <View testID={`sof_review_all_rejected_${statementId}`} style={styles.banner}>
          <MaterialIcons name="warning-amber" size={18} color="#8A5B00" />
          <Text style={styles.bannerText}>{t('mentorEngagements.review.evidence.allRejected')}</Text>
        </View>
      ) : null}

      {ai?.rationale ? (
        <View style={styles.why}>
          <Pressable accessibilityRole="button" accessibilityState={{ expanded: showWhy }} onPress={() => setWhyOpen(!showWhy)} style={styles.whyHead}>
            <Text style={styles.whyTitle}>{t('mentorEngagements.review.evidence.why')}</Text>
            <View style={styles.whyMeta}>
              {ai.confidence != null ? <Text style={[styles.confidence, lowConfidence && { color: '#8A5B00' }]}>{t('mentorEngagements.review.evidence.confidence', { percent: Math.round(ai.confidence * 100) })}</Text> : null}
              <MaterialIcons name={showWhy ? 'expand-less' : 'expand-more'} size={20} color={reviewDesk.inkSoft} />
            </View>
          </Pressable>
          {showWhy ? <Text style={styles.whyBody}>{ai.rationale}</Text> : null}
        </View>
      ) : null}

      {!recorded ? (
        <View style={styles.empty}>
          <MaterialIcons name="schedule" size={18} color={reviewDesk.muted} />
          <Text style={styles.emptyText}>{t('mentorEngagements.review.evidence.unrecorded')}</Text>
        </View>
      ) : !moments.length ? (
        <View style={styles.empty}>
          <MaterialIcons name="search-off" size={18} color={reviewDesk.muted} />
          <Text style={styles.emptyText}>{t('mentorEngagements.review.evidence.none')}</Text>
        </View>
      ) : (
        <View style={styles.timeline}>
          <View style={styles.rule} />
          <View style={styles.cards}>
            {shown.map(moment => (
              <View key={moment.key} style={styles.item}>
                <View style={[styles.dot, moment.polarity === 'contradicts' && { backgroundColor: reviewDesk.amber }, lookup.isRejected(moment) && { backgroundColor: reviewDesk.lineStrong }]} />
                <EvidenceMomentCard
                  moment={moment}
                  relevance={relevanceState(moment, lookup)}
                  reason={lookup.verdictFor(moment.key)?.reason}
                  locked={locked}
                  canJump={transcriptAvailable && moment.turn_index != null}
                  pendingReason={pendingKey === moment.key}
                  onVerdict={(verdict, reason) => handleVerdict(moment, verdict, reason)}
                  onReopen={() => onReopen(moment)}
                  onJump={() => onJump(moment)}
                />
              </View>
            ))}
            {hidden > 0 || (expanded && collapsible) ? (
              <Pressable accessibilityRole="button" onPress={onToggleExpanded} style={({ pressed }) => [styles.more, pressed && mentorStyles.pressed]}>
                <Text style={styles.moreText}>{hidden > 0 ? t('mentorEngagements.review.evidence.showMore', { count: hidden }) : t('mentorEngagements.review.evidence.showLess')}</Text>
              </Pressable>
            ) : null}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 12, minWidth: 0 },
  titleRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  count: { color: reviewDesk.muted, fontSize: 13, fontWeight: '600' },
  banner: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, backgroundColor: reviewDesk.amberSoft, borderWidth: 1, borderColor: '#F0D9A8' },
  bannerText: { flex: 1, color: '#8A5B00', fontSize: 13, fontWeight: '700', lineHeight: 19 },
  why: { borderRadius: 12, backgroundColor: reviewDesk.accentWash, borderWidth: 1, borderColor: reviewDesk.line, padding: 12, gap: 8 },
  whyHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  whyTitle: { color: reviewDesk.ink, fontSize: 13, fontWeight: '800' },
  whyMeta: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  confidence: { color: reviewDesk.muted, fontSize: 12, fontWeight: '700' },
  whyBody: { color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 21 },
  empty: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, borderRadius: 12, backgroundColor: reviewDesk.cardMuted },
  emptyText: { flex: 1, color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 20 },
  timeline: { flexDirection: 'row', gap: 10, minWidth: 0 },
  rule: { width: 2, backgroundColor: reviewDesk.line, marginLeft: 4, borderRadius: 1 },
  cards: { flex: 1, gap: 12, minWidth: 0 },
  item: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, minWidth: 0 },
  dot: { width: 10, height: 10, borderRadius: 5, backgroundColor: reviewDesk.accent, marginTop: 16, marginLeft: -15 },
  more: { alignSelf: 'flex-start', minHeight: 34, justifyContent: 'center', paddingHorizontal: 12, borderRadius: 999, borderWidth: 1, borderColor: reviewDesk.line, backgroundColor: reviewDesk.card },
  moreText: { color: reviewDesk.accent, fontSize: 12, fontWeight: '700' },
});
